import { Ic, icons } from "../../components/Icons";
import useReveal from "../../components/useReveal";
import { APP_STORE_URL, GOOGLE_PLAY_URL } from "../../config/storeLinks";
import SectionEyebrow from "./SoftwarePageEyebrow";
import AppScreensCarousel from "./AppScreensCarousel";
import ResponsiveHeroVideo from "../../components/ResponsiveHeroVideo";

const ATENO_APP_LOGO = `data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 240 198"><defs><linearGradient id="top" x1="52" x2="188" y1="50" y2="80" gradientUnits="userSpaceOnUse"><stop stop-color="%2396A7F4"/><stop offset="1" stop-color="%239B7BD2"/></linearGradient><linearGradient id="bottom" x1="28" x2="212" y1="112" y2="140" gradientUnits="userSpaceOnUse"><stop stop-color="%236476ED"/><stop offset="1" stop-color="%23744BB2"/></linearGradient></defs><rect x="52" y="42" width="136" height="40" rx="20" fill="url(%23top)"/><rect x="28" y="104" width="184" height="42" rx="21" fill="url(%23bottom)"/></svg>`;

const SOFTWARE_PRODUCTS = [
  {
    title: "Ateno Studio",
    headline: "Redesign the room you already have.",
    desc: "Scan a real room, choose a design direction, and turn the result into a polished interior concept with furniture that fits.",
  },
];

const SOFTWARE_METRICS = [
  {
    value: "1",
    label: "room scan",
    desc: "Begin with the real space instead of a blank template.",
    icon: icons.scan,
    accent: "#0ea5e9",
  },
  {
    value: "4",
    label: "guided stages",
    desc: "Move from brief to choices, contrast, and spatial review.",
    icon: icons.wand,
    accent: "#8b5cf6",
  },
  {
    value: "2",
    label: "core app flow",
    desc: "One focused experience from scan to redesign, with no extra tools to learn.",
    icon: icons.software,
    accent: "#10b981",
  },
  {
    value: "3D",
    label: "preview context",
    desc: "Review the finished room as a space, not just a flat image.",
    icon: icons.cube,
    accent: "#f97316",
  },
];

const APP_ROOM_TYPES = [
  {
    title: "Bedrooms",
    desc: "Test mood, lighting, palettes, and furniture direction in the rooms people redesign most often.",
    image: "/assets/app/rooms/bedroom.webp",
    icon: (
      <>
        <rect x="3" y="8" width="18" height="8" rx="2" />
        <path d="M6 8V6.8c0-1 .8-1.8 1.8-1.8h2.4c1 0 1.8.8 1.8 1.8V8" />
        <circle cx="16.7" cy="12" r=".9" fill="currentColor" stroke="none" />
      </>
    ),
    accent: "#8b5cf6",
  },
  {
    title: "Living spaces",
    desc: "Explore cleaner layouts and style directions before buying furniture or moving heavy pieces.",
    image: "/assets/app/rooms/living_room.webp",
    icon: (
      <>
        <rect x="4" y="10" width="16" height="5" rx="2" />
        <path d="M6.5 10V8.5c0-.9.7-1.5 1.5-1.5h8c.9 0 1.5.6 1.5 1.5V10" />
        <path d="M6.8 15v2.2M17.2 15v2.2" />
      </>
    ),
    accent: "#0ea5e9",
  },
  {
    title: "Work corners",
    desc: "Turn small spaces into focused, practical setups with a clearer sense of scale and intent.",
    image: "/assets/app/rooms/office.webp",
    icon: (
      <>
        <rect x="4" y="5" width="16" height="9" rx="1.8" />
        <path d="M12 14v5M8.5 19h7" />
      </>
    ),
    accent: "#10b981",
  },
];

const APP_STYLE_LIBRARY_ROWS = [
  {
    label: "Room types",
    speed: "34s",
    items: [
      { title: "Living Room", desc: "Sofa-led layouts", image_bg: "/assets/app/rooms/living_room.webp", accent: "#38bdf8" },
      {
        title: "Bedroom",
        desc: "Calm sleep spaces",
        image_bg: "/assets/app/rooms/bedroom.webp",
        accent: "#a78bfa",
      },
      {
        title: "Kitchen",
        desc: "Practical zones",
        image_bg: "/assets/app/rooms/kitchen.webp",
        accent: "#f59e0b",
      },
      {
        title: "Bathroom",
        desc: "Clean finishes",
        image_bg: "/assets/app/rooms/bathroom.webp",
        accent: "#67e8f9",
      },
      {
        title: "Office",
        desc: "Focused work corners",
        image_bg: "/assets/app/rooms/office.webp",
        accent: "#34d399",
      },
      {
        title: "Dining Room",
        desc: "Gathering spaces",
        image_bg: "/assets/app/rooms/dining_room.webp",
        accent: "#fb7185",
      },
    ],
  },
  {
    label: "Palettes",
    speed: "42s",
    items: [
      { title: "Warm Neutrals", desc: "Cream, tan, walnut", colors: ["#e6d7c5", "#c0a082", "#8a5e3c", "#f7efd8"], accent: "#c08457" },
      { title: "Cool Blues", desc: "Navy to pale sky", colors: ["#10243f", "#2d5b88", "#7da8cb", "#edf4fb"], accent: "#60a5fa" },
      { title: "Minimal Greys", desc: "Charcoal to white", colors: ["#4a4a4a", "#797979", "#c7c7c7", "#f5f5f5"], accent: "#94a3b8" },
      { title: "Forest", desc: "Moss and soft ivory", colors: ["#274b19", "#5f8b3c", "#bdd4a0", "#f4edd7"], accent: "#84cc16" },
      { title: "Soft Pastels", desc: "Lilac, cream, blush", colors: ["#c9c3ef", "#f2ead2", "#fff6df", "#f7e2e6"], accent: "#c4b5fd" },
      { title: "Ocean", desc: "Teal to seafoam", colors: ["#1d7375", "#56a6ad", "#a9dce1", "#eef9fb"], accent: "#22d3ee" },
    ],
  },
  {
    label: "Budgets",
    speed: "30s",
    reverse: true,
    items: [
      {
        title: "Low",
        desc: "<$1k",
        image: "/assets/app/budget/low.webp",
        imageLayout: "side",
        imagePosition: "center 63%",
        accent: "#22c55e",
      },
      {
        title: "Medium",
        desc: "$1k-5k",
        image: "/assets/app/budget/medium.webp",
        imageLayout: "side",
        imagePosition: "center 72%",
        accent: "#38bdf8",
      },
      {
        title: "High",
        desc: "$5k-15k",
        image: "/assets/app/budget/high.webp",
        imageLayout: "side",
        imagePosition: "center 58%",
        accent: "#a78bfa",
      },
      {
        title: "Premium",
        desc: ">$15k",
        image: "/assets/app/budget/premium.webp",
        imageLayout: "side",
        imagePosition: "center 66%",
        accent: "#f59e0b",
      },
    ],
  },
  {
    label: "Design styles",
    speed: "38s",
    reverse: true,
    items: [
      { title: "Modern", desc: "Clean, warm, edited", image: "/assets/app/style-cards/modern-card.webp", accent: "#60a5fa" },
      { title: "Minimalist", desc: "Quiet and precise", image: "/assets/app/style-cards/minimalist-card.webp", accent: "#cbd5e1" },
      { title: "Industrial", desc: "Raw texture", image: "/assets/app/style-cards/industrial-card.webp", accent: "#f97316" },
      { title: "Scandinavian", desc: "Airy daylight", image: "/assets/app/style-cards/scandinavian-card.webp", accent: "#facc15" },
      { title: "Bohemian", desc: "Layered personality", image: "/assets/app/style-cards/boho-card.webp", accent: "#f472b6" },
      { title: "Classic", desc: "Timeless detail", image: "/assets/app/style-cards/classic-card.webp", accent: "#fbbf24" },
    ],
  },
];

const APP_CONFIDENCE_POINTS = [
  {
    title: "No blank canvas",
    desc: "The workflow starts from the actual room, so ideas stay tied to the space users already have.",
    icon: icons.scan,
    accent: "#0ea5e9",
  },
  {
    title: "Personal inputs",
    desc: "Preferences guide the design direction, helping outputs feel intentional instead of generic.",
    icon: icons.wand,
    accent: "#8b5cf6",
  },
  {
    title: "Preview first",
    desc: "Users can compare, review, and step into the concept before committing to real changes.",
    icon: icons.cube,
    accent: "#f97316",
  },
];

export default function AppPlatformPage() {
  useReveal();

  return (
    <div className="product-family-page software-page">
      <section className="product-family-hero product-family-hero-app photo-section" id="app">
        <div className="product-family-hero-video-wrap" aria-hidden="true">
          <ResponsiveHeroVideo
            className="product-family-hero-video"
            desktopSrc="/assets/media/app/redesign-optimized.mp4"
            mobileSrc="/assets/media/app/redesign-mobile.mp4"
            poster="/assets/media/app/redesign-poster.jpg"
          />
        </div>
        <div className="product-family-hero-overlay" />
        <div className="container product-family-hero-content">
          <SectionEyebrow icon="software">Interior design app</SectionEyebrow>
          <h1 className="reveal hero-headline">Interior design for everyone.</h1>
          <p className="reveal product-family-subhead">
            Ateno Studio is the mobile app for redesigning real rooms, exploring styles, and turning everyday spaces into clear interior concepts.
          </p>
          <div className="reveal product-family-cta-row">
            <a href={APP_STORE_URL} className="btn btn-white btn-lg" target="_blank" rel="noopener noreferrer">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" style={{ marginRight: 6, verticalAlign: 'middle' }}><path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.8-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/></svg>
              App Store
            </a>
            <a href={GOOGLE_PLAY_URL} className="btn btn-lg product-family-btn-ghost" target="_blank" rel="noopener noreferrer">
              <svg width="20" height="20" viewBox="0 0 26 24" fill="none" style={{ marginRight: 6, verticalAlign: 'middle' }}><path d="M3.18 23.5c-.34-.19-.55-.56-.55-1.06V1.56c0-.5.21-.87.55-1.06l.12-.06 12.27 12.27v.29L3.3 23.56l-.12-.06Z" fill="#EA4335"/><path d="M19.63 16.45l-4.06-4.06v-.29l4.06-4.06.09.05 4.81 2.73c1.37.78 1.37 2.05 0 2.83l-4.81 2.74-.09.06Z" fill="#FBBC05"/><path d="M19.72 16.39 15.57 12.2 3.18 23.5c.45.48 1.19.54 2.02.06l14.52-7.17Z" fill="#34A853"/><path d="M19.72 8.01 5.2.84C4.37.36 3.63.42 3.18.9l12.39 11.3 4.15-4.19Z" fill="#4285F4"/></svg>
              Google Play
            </a>
          </div>
        </div>
      </section>

      <section className="section section-dark" id="workflow">
        <div className="container" style={{ textAlign: "center" }}>
          <div style={{ maxWidth: 760, margin: "0 auto 26px" }}>
            <SectionEyebrow icon="workflow">Design modes</SectionEyebrow>
            <h2 className="reveal headline" style={{ marginBottom: 14, fontSize: "clamp(28px,4vw,42px)" }}>Choose how much you want to change.</h2>
            <p className="reveal reveal-d1 subhead" style={{ marginBottom: 0 }}>
              Every redesign starts from your real room. Pick a full rethink or a lighter refresh based on your goal.
            </p>
          </div>
          <div className="grid-2" style={{ gap: 14, maxWidth: 980, margin: "0 auto" }}>
            <article
              className="reveal card-dark"
              style={{
                padding: "26px 24px",
                minHeight: 296,
                display: "flex",
                flexDirection: "column",
                justifyContent: "flex-end",
                textAlign: "left",
                backgroundImage: 'linear-gradient(180deg, rgba(6,8,14,.38), rgba(6,8,14,.88)), url("/assets/app/scale/reimagine-centered.webp")',
                backgroundSize: "cover",
                backgroundPosition: "center 72%",
                backgroundRepeat: "no-repeat",
                borderColor: "rgba(255,255,255,.14)",
                borderRadius: 20,
                boxShadow: "none",
                backdropFilter: "none",
                WebkitBackdropFilter: "none",
              }}
            >
              <div style={{ marginTop: "auto", transform: "translateY(10px)" }}>
                <div style={{ fontSize: 12, letterSpacing: ".08em", textTransform: "uppercase", color: "rgba(245,245,247,.72)", marginBottom: 10 }}>Pro</div>
                <h3 style={{ fontSize: 21, margin: 0, marginBottom: 10, color: "#f5f5f7" }}>Reimagine</h3>
                <p style={{ fontSize: 14, lineHeight: 1.7, color: "rgba(245,245,247,.86)", margin: 0 }}>
                  Generate 3 distinct redesign concepts that fully rethink your space with new style direction, layout ideas, and furniture combinations.
                </p>
              </div>
            </article>
            <article
              className="reveal reveal-d1 card-dark"
              style={{
                padding: "26px 24px",
                minHeight: 296,
                display: "flex",
                flexDirection: "column",
                justifyContent: "flex-end",
                textAlign: "left",
                backgroundImage: 'linear-gradient(180deg, rgba(10,14,19,.4), rgba(10,14,19,.88)), url("/assets/app/scale/refresh-centered.webp")',
                backgroundSize: "cover",
                backgroundPosition: "center 74%",
                backgroundRepeat: "no-repeat",
                borderColor: "rgba(255,255,255,.14)",
                borderRadius: 20,
                boxShadow: "none",
                backdropFilter: "none",
                WebkitBackdropFilter: "none",
              }}
            >
              <div style={{ marginTop: "auto", transform: "translateY(10px)" }}>
                <div style={{ fontSize: 12, letterSpacing: ".08em", textTransform: "uppercase", color: "rgba(245,245,247,.72)", marginBottom: 10 }}>Standard</div>
                <h3 style={{ fontSize: 21, margin: 0, marginBottom: 10, color: "#f5f5f7" }}>Refresh</h3>
                <p style={{ fontSize: 14, lineHeight: 1.7, color: "rgba(245,245,247,.86)", margin: 0 }}>
                  Make a light update that preserves your room identity while improving mood, materials, and overall visual cohesion.
                </p>
              </div>
            </article>
          </div>
        </div>
      </section>

      <AppScreensCarousel />

      <section className="section software-metrics-section" id="metrics">
        <div className="container">
          <SectionEyebrow icon="appFeatures">Why it converts</SectionEyebrow>
          <div className="software-metrics-head">
            <h2 className="reveal">Designed to shorten the path from idea to decision.</h2>
            <p className="reveal reveal-d1">Each step keeps the user inside a clear visual workflow, so the product feels useful before the room changes in real life.</p>
          </div>
          <div className="software-metrics-grid">
            {SOFTWARE_METRICS.map((metric, index) => (
              <article key={metric.label} className={`reveal reveal-d${(index % 4) + 1} software-metric-card`} style={{ "--metric-accent": metric.accent }}>
                <div className="software-metric-icon">
                  <Ic d={metric.icon} size={18} sw={1.9} />
                </div>
                <div className="software-metric-value">{metric.value}</div>
                <div className="software-metric-label">{metric.label}</div>
                <p>{metric.desc}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* TODO: Restore this section only after its product claims are verified. */}

      <section className="section app-story-section app-style-section" id="style-library">
        <div className="container">
          <div className="app-story-head app-story-head-left">
            <h2 className="reveal">Give the model taste, not just a prompt.</h2>
            <p className="reveal reveal-d1">Users can move from broad direction to clear visual language with palette and style choices that feel easy to compare.</p>
          </div>
        </div>
        <div className="reveal reveal-d2 app-style-stack" aria-label="Ateno Studio style, room, palette, and budget options">
          {APP_STYLE_LIBRARY_ROWS.map((row, rowIndex) => (
            <div
              key={row.label}
              className={`reveal reveal-d${(rowIndex % 3) + 1} app-style-row ${row.reverse ? "app-style-row-reverse" : ""}`}
              style={{ "--row-duration": row.speed }}
            >
              <div className="app-style-marquee">
                <div className="app-style-track">
                  {[...row.items, ...row.items, ...row.items, ...row.items].map((item, itemIndex) => {
                    const usesBudgetBackground = item.imageLayout === "side";
                    let cardStyle = {
                      "--app-accent": item.accent,
                      "--card-index": itemIndex % row.items.length,
                    };
                    
                    if (usesBudgetBackground) {
                      cardStyle["--budget-bg-position"] = item.imagePosition;
                      cardStyle.backgroundImage = `linear-gradient(90deg, rgba(0, 0, 0, .78) 0%, rgba(0, 0, 0, .58) 44%, rgba(0, 0, 0, .18) 100%), url(${item.image})`;
                    } else if (item.image) {
                      cardStyle.backgroundImage = `url(${item.image})`;
                    } else if (item.image_bg) {
                      cardStyle.backgroundImage = `url(${item.image_bg})`;
                    } else if (item.colors) {
                      const gradientStops = item.colors.map((color, idx) => `${color} ${(idx / (item.colors.length - 1)) * 100}%`).join(", ");
                      cardStyle.backgroundImage = `linear-gradient(90deg, ${gradientStops})`;
                    }
                    
                    return (
                      <article
                        key={`${row.label}-${item.title}-${itemIndex}`}
                        className={`app-style-card ${usesBudgetBackground ? "is-budget" : item.image || item.image_bg ? "is-photo" : item.colors ? "is-palette" : "is-icon"}`}
                        style={cardStyle}
                        aria-hidden={itemIndex >= row.items.length * 2}
                        aria-label={item.title}
                      >
                        {!item.image && !item.image_bg && !item.colors && (
                          <div className="app-style-option-icon" aria-hidden="true">
                            <Ic d={item.icon} size={44} sw={1.55} />
                          </div>
                        )}
                        {!item.colors && (
                          <div className="app-style-content">
                            <h3>{item.title}</h3>
                            <p>{item.desc}</p>
                          </div>
                        )}
                      </article>
                    );
                  })}
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* TODO: Restore this section only after its product claims are verified. */}

      <section className="section software-product-section" id="products">
        <div className="container">
          <div className="reveal software-product-single">
            {SOFTWARE_PRODUCTS.map((item, index) => (
              <article
                key={item.title}
                className={`reveal reveal-d${index + 1} software-product-card software-product-card-apple`}
              >
                <div className="software-product-card-main">
                  <div className="software-product-copy">
                    <div className="software-product-card-head">
                      <div className="software-product-app-logo" aria-hidden="true">
                        <img src={ATENO_APP_LOGO} alt="" />
                      </div>
                      <div>
                        <h2 className="software-product-title">{item.title}</h2>
                      </div>
                    </div>
                    <h3 className="software-product-headline">{item.headline}</h3>
                    <p className="software-product-desc">{item.desc}</p>
                    <div className="software-product-action-stack">
                      <div className="software-product-cta-group">
                        <a className="btn btn-lg software-product-primary-cta" href={APP_STORE_URL} target="_blank" rel="noopener noreferrer">
                          Start designing
                          <Ic d={icons.arrow} size={16} sw={2} />
                        </a>
                      </div>
                      <div className="software-product-store-group">
                        <span className="software-product-store-label">Available on</span>
                        <div className="software-store-logo-row" aria-label="Available on App Store and Google Play">
                          <a className="software-store-logo-chip" href={APP_STORE_URL} target="_blank" rel="noopener noreferrer">
                            <img src="/assets/logos/store/apple.svg" alt="" loading="lazy" decoding="async" aria-hidden="true" />
                            <span>App Store</span>
                          </a>
                          <a className="software-store-logo-chip" href={GOOGLE_PLAY_URL} target="_blank" rel="noopener noreferrer">
                            <img src="/assets/logos/store/google_play.png" alt="" loading="lazy" decoding="async" aria-hidden="true" />
                            <span>Google Play</span>
                          </a>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="software-product-device-placeholder" aria-label="Ateno Studio app preview">
                    <img src="/assets/devices/phone.png" alt="" loading="lazy" decoding="async" aria-hidden="true" />
                  </div>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

    </div>
  );
}
